import { useGetModalStateQuery } from '@kijiji/generated/graphql-types'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useCallback } from 'react'

import { isUserAuthenticated } from '@/features/auth/constants/user'
import { useSignInModal } from '@/features/auth/hooks/useSignInModal'
import { type MODAL_REGISTRY, MODAL_AUTH_REQUIRED } from '@/features/modal/constants/modalRegistry'
import { closeModal } from '@/features/modal/utils/closeModal'
import { openModal } from '@/features/modal/utils/openModal'

export type UseModalResponse = {
  isOpen: boolean
  handleOpenModal: () => void
  handleCloseModal: () => void
}

export const useModal = (modalId: MODAL_REGISTRY): UseModalResponse => {
  const { asPath } = useRouter()
  const { status } = useSession()
  const { openSignInModal } = useSignInModal()

  const { data, client } = useGetModalStateQuery({
    variables: { id: modalId },
    fetchPolicy: 'cache-only',
  })

  const handleOpenModal = useCallback(() => {
    /** Gets the auth variant if modal needs authentication */
    const authModalVariant = MODAL_AUTH_REQUIRED[modalId]

    if (!!authModalVariant && !isUserAuthenticated(status)) {
      openSignInModal({ modalVariant: authModalVariant, callbackUrl: asPath })
      return
    }

    openModal(client.cache, modalId)
  }, [asPath, client.cache, modalId, openSignInModal, status])

  const handleCloseModal = useCallback(
    () => closeModal(client.cache, modalId),
    [client.cache, modalId]
  )

  return {
    isOpen: data?.modal.isOpen ?? false,
    handleOpenModal,
    handleCloseModal,
  }
}
